import { Link } from '@tanstack/react-router'
import { hiddenLabel } from '@/lib/follow'
import { useUi } from '@/state/ui'
import { Check } from 'lucide-react'
import { usePeople, useSettings } from '@/db/hooks'
import { personFor } from '@/lib/people'
import { labelWords } from '@/lib/words'
import { PersonChip } from './PersonBits'
import type { SuggestionResource } from '@/kinds/suggestion'
import { acknowledgeCapture } from '@/lib/suggestions'
import { labelStyle } from '@/lib/colour'
import { fmtRemind } from '@/components/RemindPicker'
import { Tag, cx } from '@/components/ui'

/**
 * Who a capture came from: the person, as everywhere else, when the handle
 * names someone; the label in words otherwise (and any others the Shortcut set).
 */
export const CaptureSource = ({ s }: { s: SuggestionResource }) => {
  const settings = useSettings()
  const sender = personFor(usePeople(), s.labels.from)
  const entries = Object.entries(s.labels).filter(([k]) => !hiddenLabel(k) && !(k === 'from' && sender))
  if (!entries.length && !sender) return null
  return (
    <span className="inline-flex flex-wrap gap-1">
      {sender && <PersonChip person={sender} />}
      {entries.map(([k, v]) => {
        const st = labelStyle(k, v, settings.label_styles)
        return (
          <Tag key={k} style={st.style}>
            {st.icon ? `${st.icon} ` : ''}
            {k === 'from' ? v : labelWords(k, v)}
          </Tag>
        )
      })}
    </span>
  )
}

/** The receipt title ("Tomorrow · Home · 15m") as small pills. */
const OutcomePills = ({ title, dim }: { title: string; dim: boolean }) => (
  <span className="inline-flex flex-wrap gap-1">
    {title
      .split(/\s+·\s+/)
      .filter(Boolean)
      .map((part, i) => (
        <span
          key={i}
          className={cx(
            'rounded-md px-1.5 py-0.5 text-[11px] leading-4',
            dim ? 'bg-raised/60 text-faint' : 'bg-accent-soft text-fg',
          )}
        >
          {part}
        </span>
      ))}
  </span>
)

/**
 * One capture receipt: who sent it, what they said, where it went. The card
 * opens the receipt; the tick marks it seen without touching the task.
 */
export const CaptureRow = ({ s }: { s: SuggestionResource }) => {
  const reverted = s.status.state === 'reverted'
  const seen = !!s.status.acknowledged_at
  return (
    <li className="relative" data-capture-id={s.id} data-acknowledged={seen ? 'yes' : 'no'}>
      <Link to="/captures/$id" params={{ id: s.id }} className={cx('block px-3.5 py-3', !seen && 'pr-24')}>
        <div className="flex items-center gap-2 text-[11px] text-faint mb-1">
          <CaptureSource s={s} />
          <span>{s.status.decided_at ? fmtRemind(s.status.decided_at) : ''}</span>
          {reverted && (
            <span className="rounded-md bg-raised px-1.5 py-0.5 text-[11px] font-semibold">Put back</span>
          )}
        </div>
        <p className={cx('text-[15px] leading-snug line-clamp-2', reverted ? 'text-muted' : 'text-fg')}>
          {s.spec.prompt || s.spec.title}
        </p>
        {/* where it went, unless that only repeats their words */}
        {s.spec.prompt && s.spec.title.trim() !== s.spec.prompt.trim() && (
          <div className="mt-1.5 flex items-center gap-1.5">
            <span className="text-faint text-xs">→</span>
            <OutcomePills title={s.spec.title} dim={reverted} />
          </div>
        )}
      </Link>
      {!seen && (
        <button
          type="button"
          onClick={() =>
            void acknowledgeCapture(s).catch((e: Error) =>
              useUi.getState().showFlash(e.message || 'That would not clear', 4000),
            )
          }
          className="absolute right-2.5 top-2.5 min-h-9 px-3 rounded-full bg-raised text-muted text-xs font-medium hover:text-fg hover:bg-line inline-flex items-center gap-1.5 active:scale-95 transition-transform"
          title="Mark it seen; the task stays as it is"
          data-testid="capture-ack"
        >
          <Check size={14} aria-hidden /> Got it
        </button>
      )}
    </li>
  )
}
